import React, {useCallback, useEffect, useRef, useState, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {QRCodeSVG} from "qrcode.react";
import {Modal} from "components/Modal";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {Preloader} from "components/Preloader";
import {ReactNumberInput} from "components/ReactNumberInput";
import {InputsCode} from "components/InputsCode";
import {getBankIDStatus} from "helpers/helper";
import {
  cancelHsParticipantBankIdAuth,
  cancelHsParticipantSmsAuth,
  completeHsParticipantBankIdAuth,
  pollHsParticipantBankIdQrCode,
  prepareHsParticipantBankIdAuth,
  sendHsParticipantSmsAuth,
  verifyHsParticipantSmsAuth,
} from "../api/contracts";
import {signContractIdentifyMethod, signContractSmsVerifyApiError} from "../constants";

export const SignContractAuthenticateModal = ({
  contractId,
  participantId,
  emailToken,
  identifyMethod,
  onOpenApp,
  onAuthenticated,
  onClose,
}) => {
  const {t} = useTranslation();

  const [orderRef, setOrderRef] = useState(null);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [autoStartToken, setAutoStartToken] = useState(null);
  const [bankIdPoll, setBankIdPoll] = useState(null);
  const [prepareError, setPrepareError] = useState(null);
  const intervalRef = useRef(null);
  const bankIdCompleteRef = useRef(false);
  const pollInFlightRef = useRef(false);
  const onAuthenticatedRef = useRef(onAuthenticated);
  onAuthenticatedRef.current = onAuthenticated;

  const isBankId = identifyMethod === signContractIdentifyMethod.BANKID;
  const hasIds = Boolean(contractId && participantId);

  const [phone, setPhone] = useState("");
  const [isValidPhone, setIsValidPhone] = useState(false);
  const [smsSending, setSmsSending] = useState(false);
  const [smsError, setSmsError] = useState(null);
  const [smsCodeSent, setSmsCodeSent] = useState(false);
  const [smsDigits, setSmsDigits] = useState(["", "", "", ""]);
  const [smsVerifying, setSmsVerifying] = useState(false);
  const [smsSessionExpired, setSmsSessionExpired] = useState(false);
  const [smsCodeInputsKey, setSmsCodeInputsKey] = useState(0);

  const smsCode = smsDigits.join("");
  const isSmsCodeComplete = smsCode.length === 4;
  const smsRequestIdKey = `hs_sms_request_id_${contractId}_${participantId}`;

  const userMessage = bankIdPoll?.userMessage;
  const statusLine = userMessage ? getBankIDStatus(userMessage) : null;

  const values = useMemo(
    () => [contractId, participantId, emailToken],
    [contractId, participantId, emailToken]
  );

  const clearPoll = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const handleClose = useCallback(async () => {
    clearPoll();
    if (hasIds && isBankId)
      await cancelHsParticipantBankIdAuth(...values, orderRef ? {orderRef} : {})
        .catch(() => cancelHsParticipantBankIdAuth(...values, {}))
        .catch(() => {});
    else if (hasIds)
      await cancelHsParticipantSmsAuth(...values, {
        request_id: localStorage.getItem(smsRequestIdKey) || undefined,
      })
        .catch(() => cancelHsParticipantSmsAuth(...values, {}))
        .catch(() => {})
        .finally(() => localStorage.removeItem(smsRequestIdKey));
    await Promise.resolve(onClose?.());
  }, [clearPoll, values, hasIds, isBankId, onClose, orderRef, smsRequestIdKey]);

  const handleSendSms = useCallback(async () => {
    if (!hasIds || !phone.length || !isValidPhone || smsSending) return;
    setSmsError(null);
    setSmsSending(true);
    try {
      const data = await sendHsParticipantSmsAuth(...values, {
        phoneNumber: phone,
        request_id: localStorage.getItem(smsRequestIdKey),
      });
      if (data.request_id) localStorage.setItem(smsRequestIdKey, data.request_id);
      setSmsSessionExpired(false);
      setSmsCodeSent(true);
      setSmsDigits(["", "", "", ""]);
      setSmsSending(false);
    } catch (_e) {
      setSmsError(t("SIGN_CONTRACT_AUTHENTICATE_SMS_SEND_FAILED"));
      setSmsSending(false);
    }
  }, [values, hasIds, isValidPhone, phone, smsSending, smsRequestIdKey, t]);

  const handleVerifySms = useCallback(async () => {
    if (!hasIds || !isSmsCodeComplete || smsVerifying) return;
    setSmsError(null);
    setSmsVerifying(true);
    try {
      await verifyHsParticipantSmsAuth(...values, {
        code: smsCode,
        request_id: localStorage.getItem(smsRequestIdKey),
      });
      localStorage.removeItem(smsRequestIdKey);
      onAuthenticated();
    } catch (e) {
      const apiError = e?.response?.data?.error;
      if (apiError === signContractSmsVerifyApiError.SESSION_EXPIRED) {
        setSmsSessionExpired(true);
        setSmsError(t("SIGN_CONTRACT_AUTHENTICATE_SMS_CODE_EXPIRED"));
      } else {
        setSmsError(t("SIGN_CONTRACT_AUTHENTICATE_SMS_VERIFY_FAILED"));
        setSmsDigits(["", "", "", ""]);
        setSmsCodeInputsKey((k) => k + 1);
      }
      setSmsVerifying(false);
    }
  }, [
    values,
    hasIds,
    isSmsCodeComplete,
    onAuthenticated,
    smsCode,
    smsRequestIdKey,
    smsVerifying,
    t,
  ]);

  const handleResendSms = useCallback(async () => {
    if (!hasIds) return;
    setSmsError(null);
    setSmsSending(true);
    try {
      const data = await sendHsParticipantSmsAuth(...values, {
        phoneNumber: phone,
        request_id: localStorage.getItem(smsRequestIdKey),
      });
      if (data.request_id) {
        localStorage.setItem(smsRequestIdKey, data.request_id);
      }
      setSmsSessionExpired(false);
      setSmsDigits(["", "", "", ""]);
      setSmsCodeInputsKey((k) => k + 1);
      setSmsSending(false);
    } catch (_e) {
      setSmsError(t("SIGN_CONTRACT_AUTHENTICATE_SMS_SEND_FAILED"));
      setSmsSending(false);
    }
  }, [values, hasIds, phone, smsRequestIdKey, t]);

  useEffect(() => {
    if (!isBankId || !hasIds) return;
    let cancelled = false;

    prepareHsParticipantBankIdAuth(...values)
      .then((data) => {
        if (cancelled) return;
        setAutoStartToken(data.autoStartToken);
        setOrderRef(data.orderRef);
      })
      .catch(
        () =>
          !cancelled &&
          setPrepareError(t("SIGN_CONTRACT_AUTHENTICATE_BANKID_PREPARE_FAILED"))
      );

    return () => {
      cancelled = true;
    };
  }, [values, hasIds, isBankId, t]);

  useEffect(() => {
    if (!isBankId || !hasIds || !orderRef) return undefined;

    bankIdCompleteRef.current = false;

    const tick = () => {
      // a poll slower than the interval would collect the same order twice,
      // and the second collect fails once the first one has consumed it
      if (pollInFlightRef.current) return;
      pollInFlightRef.current = true;

      pollHsParticipantBankIdQrCode(...values, {orderRef})
        .then((data) => {
          setQrCodeData(data.qrCodeData || null);
          setBankIdPoll(data);

          if (data.completionData) {
            if (bankIdCompleteRef.current) return;
            bankIdCompleteRef.current = true;
            clearPoll();
            completeHsParticipantBankIdAuth(...values, {
              completionData: data.completionData,
            })
              .then(() => onAuthenticatedRef.current?.())
              .catch(() => {
                bankIdCompleteRef.current = false;
              });
            return;
          }

          if (data.status === "failed") {
            clearPoll();
            cancelHsParticipantBankIdAuth(...values, {orderRef}).catch(() => {});
          }
        })
        .catch(() => clearPoll())
        .finally(() => {
          pollInFlightRef.current = false;
        });
    };

    tick();
    intervalRef.current = setInterval(tick, 1000);

    return () => {
      clearPoll();
    };
  }, [clearPoll, values, hasIds, isBankId, orderRef]);

  return (
    <Modal
      noTitleIcon
      titleText={
        isBankId
          ? "SIGN_CONTRACT_AUTHENTICATE_MODAL_BANKID_TITLE"
          : "SIGN_CONTRACT_AUTHENTICATE_MODAL_SMS_TITLE"
      }
      boldTitle
      noCloseOnEsc={false}
      noCenterContent
      width={isBankId ? "320" : "450"}
      rounded
      whiteTitle
      onClose={isBankId || smsCodeSent ? handleClose : onClose}
    >
      {!isBankId && !smsCodeSent && (
        <div className="pb-8 pl-8 pr-8 __text-center">
          {smsError && <p className="red fz-13 mb-4">{smsError}</p>}
          <p className="fz-14 mb-4 mt-2">
            {t("SIGN_CONTRACT_AUTHENTICATE_SMS_PHONE_DESCRIPTION")}
          </p>

          <div className="main-page__content" style={{marginTop: 0}}>
            <div className="main-page__content__form">
              <ReactNumberInput
                autoFocus
                phone={phone}
                setPhone={setPhone}
                onEnterKeyPress={handleSendSms}
                setIsValid={setIsValidPhone}
              />
            </div>
          </div>

          <p className="fz-10 __bold __uppercase mid-gray mt-2 mb-5">
            {t("USE_A_NUMBER_THAT_CAN_RECIEVE_SMS")}
          </p>
          <div className="df mt-8">
            <ButtonTransparent
              name="CANCEL"
              medium
              onClick={onClose}
              color="red"
              addClass="mla"
            />
            <ButtonTransparent
              name="SIGN_CONTRACT_AUTHENTICATE_SMS_TEXT_ME"
              medium
              nonTransparent
              disabled={!phone.length || !isValidPhone || smsSending}
              onClick={handleSendSms}
              addClass="ml-4"
            />
          </div>
        </div>
      )}

      {!isBankId && smsCodeSent && (
        <form
          className="pb-8 pl-8 pr-8 main-page__content__auth-code __text-center"
          style={{marginTop: 0}}
          onSubmit={(e) => {
            e.preventDefault();
            if (!smsSessionExpired) handleVerifySms();
          }}
        >
          {smsError && <p className="red fz-13 mb-4">{smsError}</p>}
          <p className="fz-14 mb-2">
            {t("SIGN_CONTRACT_AUTHENTICATE_SMS_CODE_DESCRIPTION")}
          </p>

          <InputsCode
            key={smsCodeInputsKey}
            code={smsDigits}
            setCode={setSmsDigits}
            disabled={smsVerifying || (smsSessionExpired && smsSending)}
          />
          <div className="df mt-4">
            <ButtonTransparent
              name="CANCEL"
              medium
              onClick={handleClose}
              color="red"
              addClass="mla"
            />
            <ButtonTransparent
              name={
                smsSessionExpired
                  ? "SIGN_CONTRACT_AUTHENTICATE_SMS_RESEND_MY_CODE"
                  : "SIGN_CONTRACT_AUTHENTICATE_SMS_SUBMIT"
              }
              medium
              nonTransparent
              type={smsSessionExpired ? "button" : "submit"}
              disabled={
                smsSessionExpired ? smsSending : !isSmsCodeComplete || smsVerifying
              }
              onClick={smsSessionExpired ? handleResendSms : undefined}
              addClass="ml-4"
            />
          </div>
        </form>
      )}

      {isBankId && (
        <div className="pb-8 pl-8 pr-8 df-column-center">
          {prepareError && <p className="fz-14 red __text-center mb-4">{prepareError}</p>}
          {!prepareError && (
            <>
              <p className="fz-14 __text-center mb-4 mt-2">
                {statusLine || t("SIGN_CONTRACT_AUTHENTICATE_MODAL_BANKID_SCAN_TEXT")}
              </p>
              <div className="df-row-jcc-center mb-5" style={{minHeight: 160}}>
                {qrCodeData ? (
                  <QRCodeSVG value={qrCodeData} size={160} />
                ) : (
                  <Preloader addClass="hght-180 df-row-jcc-center" />
                )}
              </div>
              {qrCodeData && autoStartToken && (
                <a
                  href={`bankid:///?autostarttoken=${autoStartToken}&redirect=${encodeURIComponent(
                    window.location.href
                  )}`}
                  onClick={() => onOpenApp?.(orderRef)}
                  className="modal-identify_link fz-14"
                >
                  {t("OPEN_BANK_ID_LINK")}
                </a>
              )}
            </>
          )}
          <ButtonTransparent name="CANCEL" medium color="red" onClick={handleClose} />
        </div>
      )}
    </Modal>
  );
};
